import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';

// Components
import { Swiper } from 'dibs-pinch-zoom-swipe';
import { LazyImage } from 'dibs-search-product-tile/src/LazyImage/LazyImage';
import { FavoritesItem } from '../../components/global/FavoriteIcons/FavoritesItem';

// Helpers
import get from 'lodash.get';
import { LOCATION_SEARCH_BROWSE } from '../../components/global/TradeFavoritesTooltip/tradeFavoritesTooltipTracking';
import { getSrcsetString } from '../../utils/srcSet';

// Styles
import styles from './SharedItemTile.scss';

// Constants
import { SRC_SET_SIZES_TILE } from '../../constants/imageConstants';
import { tileMobileGridTypes } from '../../constants/sbConstants';

export function ProductPhotoItemTileComponent(props) {
    const {
        favorites,
        index,
        item,
        lazyLoadStartIndex,
        onClick,
        onContentLoaded,
        imageLoadVerticalOffset,
        imageSize,
        gridType,
    } = props;
    const numPreviewSwipes = useRef(0);
    const [startingPage, setStartingPage] = useState(1);
    const itemId = get(item, 'serviceId') || '';
    const itemTitle = get(item, 'title') || '';
    const pdpUrl = get(item, 'pdpURL') || '';
    const smallPath = get(item, 'productPhotos[0].smallPath');
    const webPath = get(item, 'productPhotos[0].versions[0].webPath') || '';
    const placeholder = get(item, 'productPhotos[0].placeholder');
    const src = smallPath || webPath;
    const masterPath = get(item, 'productPhotos[0].masterOrZoomPath');

    const srcSetAttributes = masterPath
        ? {
              srcSet: getSrcsetString(masterPath),
              sizes: SRC_SET_SIZES_TILE,
          }
        : {};

    useEffect(() => {
        // FINDING-14344: Need to slide 1st item tile back and forth twice when showing
        // product phot only grid in order to preview swiping featur to user. This is done by
        // alternating the 1st tile's swiper starting page every second, 4 consecutive times
        let previewTileInfoSwipe;
        if (index === 0 && gridType === tileMobileGridTypes.PRODUCT_PHOTO_ONLY) {
            previewTileInfoSwipe = setInterval(() => {
                if (numPreviewSwipes.current === 4) {
                    clearInterval(previewTileInfoSwipe);
                } else {
                    numPreviewSwipes.current++;
                    const newStartingPage = startingPage === 1 ? 0 : 1;
                    setStartingPage(newStartingPage);
                }
            }, 1000);
        }
        return previewTileInfoSwipe ? () => clearInterval(previewTileInfoSwipe) : () => {};
    }, [index, gridType, startingPage, setStartingPage, numPreviewSwipes]);

    return (
        <div className={styles.photoOnly}>
            <div className={styles.imageContainer} data-tn="product-image-container">
                <Swiper itemsPerPage={1} startingPage={startingPage} hasInfiniteLoop>
                    <div>
                        {favorites && (
                            <FavoritesItem
                                wrapperClass={styles.favoritesWrapper}
                                heartIconWrapperClass={styles.heartIconWrapper}
                                folderIconWrapperClass={styles.folderIconWrapper}
                                itemId={itemId}
                                itemTitle={itemTitle}
                                fetchFolder={false}
                                showTooltipOnFirstClick
                                tooltipTrackingLabel={LOCATION_SEARCH_BROWSE}
                                {...favorites}
                            />
                        )}
                        <a className={styles.titleLink} href={pdpUrl} onClick={onClick}>
                            {itemTitle}
                        </a>
                    </div>
                    <a href={pdpUrl} onClick={onClick}>
                        {index < lazyLoadStartIndex ? (
                            <img
                                className={styles.image}
                                src={src}
                                alt={itemTitle}
                                {...srcSetAttributes}
                                data-tn="product-image"
                            />
                        ) : (
                            <React.Fragment>
                                <LazyImage
                                    className={styles.image}
                                    srcSetAttributes={srcSetAttributes}
                                    placeholder={placeholder}
                                    alt={itemTitle}
                                    offsetVertical={imageLoadVerticalOffset}
                                    onImageLoad={onContentLoaded}
                                    useLoFiLazyLoader
                                    src={src}
                                    imageSize={imageSize}
                                />
                                <noscript>
                                    <img
                                        className={styles.image}
                                        src={src}
                                        alt={itemTitle}
                                        {...srcSetAttributes}
                                    />
                                </noscript>
                            </React.Fragment>
                        )}
                    </a>
                </Swiper>
            </div>
        </div>
    );
}

ProductPhotoItemTileComponent.defaultProps = {
    lazyLoadStartIndex: 2,
    gridType: tileMobileGridTypes.COMPLETE_PRODUCT_INFO,
};

ProductPhotoItemTileComponent.propTypes = {
    gridType: PropTypes.oneOf([
        tileMobileGridTypes.COMPLETE_PRODUCT_INFO,
        tileMobileGridTypes.PRODUCT_PHOTO_ONLY,
    ]),
    favorites: PropTypes.object,
    index: PropTypes.number.isRequired,
    item: PropTypes.object.isRequired,
    lazyLoadStartIndex: PropTypes.number,
    imageLoadVerticalOffset: PropTypes.number,
    onClick: PropTypes.func,
    onContentLoaded: PropTypes.func,
    imageSize: PropTypes.string,
};

export const ProductPhotoItemTile = createFragmentContainer(ProductPhotoItemTileComponent, {
    item: graphql`
        fragment ProductPhotoItemTile_item on Item {
            serviceId
            title
            pdpURL
            productPhotos: photos(limit: 1) {
                placeholder
                smallPath
                masterOrZoomPath
                versions {
                    webPath
                }
            }
        }
    `,
});
